import { useEffect, useMemo } from "react";
import type { AccentName, LayoutPreset, Me } from "@/types/api";

/**
 * Per-tenant layout/accent picker.
 *
 * Phase 1: derived deterministically from tenantId so different tenants
 * automatically look distinct out of the box. Once Tenant Settings are wired
 * up, this hook will read `tenants.settings.layoutPreset` and
 * `tenants.settings.accent` and fall back to this round-robin only if unset.
 */
const PRESETS: LayoutPreset[] = ["left-pinned", "icon-rail", "glass-floating"];
const ACCENTS: AccentName[] = ["rose", "violet", "emerald", "cyan", "amber"];

export function useTenantLayout(me: Me | undefined | null) {
  const choice = useMemo(() => {
    const tid = me?.tenantId ?? 1;
    return {
      preset: PRESETS[(tid - 1) % PRESETS.length],
      accent: ACCENTS[(tid - 1) % ACCENTS.length],
    };
  }, [me?.tenantId]);

  // Apply accent on the document root so every CSS-variable-driven component
  // updates without prop-drilling.
  useEffect(() => {
    document.documentElement.dataset.tenantAccent = choice.accent;
  }, [choice.accent]);

  return choice;
}
